import { Search, Bell, Power, User, BadgeCheck, CreditCard, Mail, Phone, MapPin, Globe, Shield } from 'lucide-react';
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./Header.module.css";
import { useSidebarContext } from "../Sidebar/context/SidebarState";
import CustomDialog from "../dialog/CustomDialog";
import { post } from "../../utils/api";

const Header = ({ userName, userId, roleId, roleName, arnCode, panNumber }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserName, setUserId } = useSidebarContext();
  const [isLogoutModalVisible, setIsLogoutModalVisible] = React.useState(false);
  const [resolvedArnCode, setResolvedArnCode] = React.useState(arnCode || "");
  const [isProfileModalVisible, setIsProfileModalVisible] = React.useState(false);
  const [profileLoading, setProfileLoading] = React.useState(false);
  const [profileData, setProfileData] = React.useState(null);

  const handleOpenProfile = async () => {
    setIsProfileModalVisible(true);
    if (!profileData) {
      try {
        setProfileLoading(true);
        const response = await post("/user/fetchUserProfile", { userId });
        if (response.data && response.data.Success) {
          setProfileData(response.data.Data);
        } else {
          console.error("Failed to fetch profile details:", response.data?.Message);
        }
      } catch (error) {
        console.error("Error fetching profile details:", error);
      } finally {
        setProfileLoading(false);
      }
    }
  };

  const ROLE_MAPPING = {
    1: "Administrator",
    2: "Partner",
    3: "Ops (AMC)",
    7: "Sales",
    8: "Sales Head"
  };

  const rawRole = roleName || ROLE_MAPPING[roleId] || "Administrator";
  const displayRole =
    Number(roleId) === 2 || String(rawRole).toLowerCase().includes("distributor")
      ? "Partner"
      : rawRole;

  React.useEffect(() => {
    setUserName(userName);
    setUserId(userId);
  }, [userName, userId, setUserName, setUserId]);

  React.useEffect(() => {
    setResolvedArnCode(arnCode || "");
  }, [arnCode]);

  React.useEffect(() => {
    const fetchDistributorArn = async () => {
      const userDetails = JSON.parse(localStorage.getItem("user_details") || "{}");
      const userEmail = userDetails?.email;

      if (Number(roleId) !== 2 || arnCode || !userEmail) {
        return;
      }

      try {
        const response = await post("/onboarding/fetchDistributorListing", {
          distributor_email: userEmail,
        });

        if (response.data?.Success && Array.isArray(response.data.Data) && response.data.Data.length > 0) {
          const distributorArn = response.data.Data[0]?.arn_code || "";
          setResolvedArnCode(distributorArn);
        } else {
          setResolvedArnCode("");
        }
      } catch (error) {
        console.error("Error fetching distributor ARN for header:", error);
        setResolvedArnCode("");
      }
    };

    fetchDistributorArn();
  }, [roleId, arnCode]);

  const handleLogout = () => {
    setIsLogoutModalVisible(true);
  };

  const confirmLogout = () => {
    localStorage.setItem("is_logged_in", false);
    localStorage.removeItem("user_details");
    localStorage.removeItem("ui_details");
    localStorage.removeItem("distributor_draft_data");
    navigate("/", { replace: true });
  };

  const toTitleCase = (str) =>
    str ? str.replace(/\w\S*/g, (txt) => txt[0].toUpperCase() + txt.substring(1).toLowerCase()) : "";

  const formatBreadcrumbLabel = (str) =>
    toTitleCase(str?.replace(/[-_]/g, " "));

  const detailPills = [
    Number(roleId) === 2 ? { label: "APRN", value: resolvedArnCode || "Not Assigned", icon: BadgeCheck } : null,
    panNumber ? { label: "PAN", value: panNumber, icon: CreditCard } : null,
  ].filter(Boolean);

  // Mock breadcrumb data based on location
  const pathnames = location.pathname.split("/").filter((x) => x);
  const currentPath = pathnames[pathnames.length - 1];
  const pageTitle = currentPath === "distributor_commercials_mapping"
    ? "Distributor Management"
    : currentPath
      ? formatBreadcrumbLabel(currentPath)
      : "Dashboard";

  return (
    <div className="h-[70px] bg-white flex items-center justify-between px-5 relative border-b border-gray-100">
      {/* LEFT SECTION: Breadcrumbs */}
      <div className="flex items-center gap-4">
        <div className="flex flex-col">
          <h1 className="text-xl font-bold text-gray-800 leading-tight">{pageTitle}</h1>
          <div className="text-xs text-gray-400 font-medium">
            Dashboard <span className="mx-1">/</span> <span className="text-indigo-500">{pageTitle}</span>
          </div>
        </div>
      </div>

      {/* CENTER SECTION: Search Bar */}
      {/* <div className="hidden md:flex flex-1 justify-center max-w-lg mx-8">
        <div className="relative w-full">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
            placeholder="Search anything..."
          />
        </div>
      </div> */}

      {/* RIGHT SECTION: Notifications + User */}
      <div className="flex items-center gap-3">
        {/* Notifications */}
        {/* <div className="relative p-2 cursor-pointer hover:bg-gray-50 rounded-full transition-colors">
          <Bell className="h-5 w-5 text-gray-500" />
          <span className="absolute top-1.5 right-1.5 h-4 w-4 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-white">
            3
          </span>
        </div> */}

        {/* User Info */}
        <div className="flex items-center gap-3">
          <div
            onClick={handleOpenProfile}
            className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white px-3 py-2 shadow-sm hover:shadow hover:border-gray-200 cursor-pointer transition-all group"
            title="Click to View Profile"
          >
            <div className="flex flex-col items-end pr-1">
              <span className="text-sm font-bold text-slate-800 leading-tight group-hover:text-indigo-600 transition-colors">
                {toTitleCase(userName)}
              </span>
              <span className="text-[10px] text-slate-400 font-semibold tracking-[0.14em] uppercase mt-0.5">
                {toTitleCase(displayRole)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div
              onClick={handleLogout}
              className="h-10 w-10 bg-gray-50 border border-gray-100 rounded-xl flex items-center justify-center text-gray-400 hover:bg-red-50 hover:text-red-500 cursor-pointer transition-all"
              title="Logout"
            >
              <Power className="h-5 w-5" />
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <CustomDialog
        visible={isLogoutModalVisible}
        onHide={() => setIsLogoutModalVisible(false)}
        header=""
        width="400px"
        closable={false}
      >
        <div className="flex flex-col items-center text-center py-2">
          {/* Power Icon in Circle */}
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
            <Power className="h-8 w-8 text-red-500" />
          </div>

          <h2 className="text-xl font-bold text-gray-900 mb-2">Confirm Logout</h2>
          <p className="text-gray-500 mb-8 px-4">
            Are you sure you want to logout from your account?
          </p>

          <div className="flex w-full gap-3">
            <button
              onClick={() => setIsLogoutModalVisible(false)}
              className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-colors border border-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={confirmLogout}
              className="flex-1 py-3 px-4 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl transition-colors shadow-lg shadow-red-100"
            >
              Logout
            </button>
          </div>
        </div>
      </CustomDialog>

      {/* Profile Details Modal */}
      <CustomDialog
        visible={isProfileModalVisible}
        onHide={() => setIsProfileModalVisible(false)}
        header=""
        width="550px"
        closable={false}
      >
        <div className="flex flex-col py-1">
          {/* Avatar Banner Card */}
          <div className="relative rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600 p-6 text-white mb-6 shadow-md overflow-hidden">
            <div className="absolute inset-0 bg-white/10 backdrop-blur-[1px]"></div>
            <div className="relative z-10 flex items-center gap-4">
              <div className="h-16 w-16 rounded-xl bg-white/25 border border-white/30 flex items-center justify-center text-2xl font-extrabold text-white">
                {profileData?.name ? profileData.name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase() : "U"}
              </div>
              <div>
                <h3 className="text-lg font-bold tracking-tight uppercase leading-snug">
                  {profileData?.name || toTitleCase(userName)}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-white border border-white/25 uppercase tracking-wide">
                    <Shield className="h-2.5 w-2.5" />
                    {displayRole}
                  </span>
                  <span className="text-[10px] text-white/70 font-semibold">User ID: #{userId}</span>
                </div>
              </div>
            </div>
          </div>

          <h4 className="text-[11px] font-bold tracking-wider text-slate-400 uppercase mb-4 px-1">
            Account Information
          </h4>

          {profileLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-100 border-t-indigo-600"></div>
              <p className="mt-3 text-xs font-semibold text-slate-400 animate-pulse">Loading Profile details...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {[
                { label: "Name", value: profileData?.name || userName, icon: User, color: "text-indigo-600 bg-indigo-50 border-indigo-100" },
                { label: "Mobile", value: profileData?.mobile_number || "N/A", icon: Phone, color: "text-emerald-600 bg-emerald-50 border-emerald-100" },
                { label: "Email Address", value: profileData?.email || "N/A", icon: Mail, color: "text-sky-600 bg-sky-50 border-sky-100" },
                { label: "PAN Number", value: String(profileData?.pan_number || panNumber || "N/A").toUpperCase(), icon: CreditCard, color: "text-rose-600 bg-rose-50 border-rose-100", isUppercase: true },
                { label: "APRN Code", value: String(profileData?.arn_code || resolvedArnCode || "N/A").toUpperCase(), icon: BadgeCheck, color: "text-teal-600 bg-teal-50 border-teal-100", isUppercase: true },
                { label: "City", value: profileData?.city || "N/A", icon: MapPin, color: "text-amber-600 bg-amber-50 border-amber-100" },
                { label: "State", value: profileData?.state || "N/A", icon: Globe, color: "text-violet-600 bg-violet-50 border-violet-100" },
              ].map((item, idx) => {
                const IconComponent = item.icon;
                return (
                  <div key={idx} className="bg-slate-50/50 rounded-xl border border-slate-100 p-3.5 flex items-center gap-3">
                    <div className={`p-2 rounded-lg border flex items-center justify-center shrink-0 ${item.color}`}>
                      <IconComponent className="h-4 w-4 stroke-[2px]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[9px] font-bold tracking-wider uppercase text-slate-400 block mb-0.5">
                        {item.label}
                      </span>
                      <span className="text-xs font-semibold text-slate-700 truncate block">
                        {item.isUppercase ? String(item.value || "N/A").toUpperCase() : toTitleCase(item.value)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => setIsProfileModalVisible(false)}
              className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-bold rounded-xl transition-colors border border-slate-200"
            >
              Close
            </button>
          </div>
        </div>
      </CustomDialog>
    </div>
  );
};

export default Header;
